/**
 * How a list page decides which sort it opens on.
 *
 * Kept apart from `useTableController` because it is a pure decision with no
 * React, store or transport dependency — the hook's own module reaches the
 * redux store at import time, and this logic is worth testing without it.
 *
 * The precedence is the same for both halves: the user's saved choice, then
 * the page's declared default, then the historical fallback.
 */

/** What a page states about how it should open. Both halves apply together. */
export interface DefaultSort {
  readonly column: string;
  readonly order: 'asc' | 'desc';
}

/**
 * Which column the table opens on.
 *
 * Both candidates are checked against the header config: a key the server no
 * longer sends would otherwise go out as a sortColumn the endpoint rejects or
 * silently ignores.
 *
 * The final fallback — the first header_key — is an accident of column order
 * rather than a decision, which is why a page that cares should say so.
 */
export const getDefaultSortColumn = (
  preferredSorting: { sortColumn?: string } | undefined,
  headerConfig: readonly { header_key?: string }[],
  defaultSort: DefaultSort | undefined
): string => {
  if (headerConfig.length === 0) return '';
  const preferred = preferredSorting?.sortColumn;
  const headerKeys = (headerConfig || []).map((header) => header.header_key);

  if (preferred && headerKeys.includes(preferred)) return preferred;
  if (defaultSort && headerKeys.includes(defaultSort.column)) return defaultSort.column;
  return headerKeys[0] || 'created_at';
};

/**
 * Which direction the table opens in, following whichever column won.
 *
 * A page asking for newest-first means `created_at` DESC, so pairing its column
 * with a hardcoded 'asc' would be worse than not asking at all. Where the
 * page's column did not win, its direction is not applied either — the pair
 * only means anything together.
 */
export const getDefaultSortOrder = (
  preferredSorting: { sortOrder?: string } | undefined,
  defaultSort: DefaultSort | undefined,
  resolvedColumn: string
): 'asc' | 'desc' => {
  const preferred = preferredSorting?.sortOrder as 'asc' | 'desc' | undefined;
  if (preferred) return preferred;
  if (defaultSort && resolvedColumn === defaultSort.column) return defaultSort.order;
  return 'asc';
};
